import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronRight, ChevronLeft } from 'lucide-react';
import esterC from "../assets/image/peakmen.avif";
import magnesium from "../assets/image/prowell.avif";
import omega3 from "../assets/image/ladybild.avif";
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from "react-i18next";
import DnaHelixCanvas from './hero/DnaHelixCanvas';

const Hero = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      badge: t("home.slides.0.badge"),
      title: t("home.slides.0.title"),
      description: t("home.slides.0.description"),
      image: esterC,
      price: "890₺",
      rating: 5.0,
      accent: "from-orange-400 to-amber-600",
      bgGradient: "from-orange-50/50 to-fecos-bg",
      buttonColor: "bg-[#FF6B00]"
    },
    {
      id: 2,
      badge: t("home.slides.1.badge"),
      title: t("home.slides.1.title"),
      description: t("home.slides.1.description"),
      image: magnesium,
      price: "650₺",
      rating: 4.9,
      accent: "from-emerald-400 to-teal-600",
      bgGradient: "from-emerald-50/50 to-fecos-bg",
      buttonColor: "bg-[#10b981]"
    },
    {
      id: 3,
      badge: t("home.slides.2.badge"),
      title: t("home.slides.2.title"),
      description: t("home.slides.2.description"),
      image: omega3,
      price: "720₺",
      rating: 5.0,
      accent: "from-blue-400 to-indigo-600",
      bgGradient: "from-blue-50/50 to-fecos-bg",
      buttonColor: "bg-[#3b82f6]"
    }
  ];

  // Auto-play (optional, disabled for better UX during dev, enable if requested)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden bg-fecos-bg">
      {/* Background Transition */}
      <AnimatePresence mode='wait'>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className={`absolute inset-0 bg-gradient-to-br ${slides[currentSlide].bgGradient} opacity-60`}
        />
      </AnimatePresence>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10 w-full pt-20 lg:pt-0">

        {/* LEFT COLUMN: Content */}
        <div className="space-y-8 order-1">
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20, position: "absolute" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-8"
            >
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-white/60 backdrop-blur-sm border border-fecos-border px-3 py-1 rounded-full shadow-sm">
                <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${slides[currentSlide].accent}`} />
                <span className="text-[11px] font-semibold text-fecos-text/70 uppercase tracking-widest">
                  {slides[currentSlide].badge}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-fecos-text leading-[1.05] tracking-tight whitespace-pre-line">
                {slides[currentSlide].title}
              </h1>

              {/* Description */}
              <p className="text-base sm:text-lg text-fecos-gray max-w-md leading-relaxed">
                {slides[currentSlide].description}
              </p>
              ...
              {/* CTAs */}
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/all-product"
                  className={`px-8 py-4 rounded-full text-white font-bold text-sm tracking-wide shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2`}
                  style={{ backgroundColor: slides[currentSlide].buttonColor.replace("bg-[", "").replace("]", "") }}
                >
                  {t("common.shopNow")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-4 rounded-full bg-white border border-fecos-border text-fecos-text font-bold text-sm tracking-wide hover:bg-gray-50 transition-all"
                >
                  {t("common.readStory")}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="flex items-center gap-4 pt-8">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full border border-fecos-border flex items-center justify-center hover:bg-white hover:shadow-md transition-all active:scale-90"
            >
              <ChevronLeft className="w-5 h-5 text-fecos-text" />
            </button>
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${currentSlide === i ? 'w-8 bg-fecos-text' : 'w-2 bg-gray-300'}`}
                />
              ))}
            </div>
            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full border border-fecos-border flex items-center justify-center hover:bg-white hover:shadow-md transition-all active:scale-90"
            >
              <ChevronRight className="w-5 h-5 text-fecos-text" />
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: Spline Integration */}
        <div className="relative w-full h-[350px] sm:h-[450px] lg:h-[700px] order-2">
          <div className="absolute inset-0 w-full h-full rounded-3xl overflow-hidden shadow-2xl border border-white/20">
            {/* Gradient Glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-orange-400/10 to-transparent pointer-events-none mix-blend-overlay z-10" />

            <DnaHelixCanvas />
          </div>
        </div>

      </div>
    </section>
  );
};

export default Hero;